import { Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

import { MessageService, FormsUtilsService, notCloseMessageOptions } from '../../../../../../core';
import { Decimal2Component } from '../../../../../../shared';
import { ClienteCentralCompraParticipante } from '../cliente-central-compra-participante';
import { TypeaheadClienteComponent } from '../../../typeahead/typeahead-cliente.component';
import { ClienteNomePipe } from '../../../pipes';
import { ClienteCentralCompraParticipanteCalculoService } from '../services';

@Component({
    selector: 'sgt-cliente-central-compra-participante-form',
    templateUrl: './cliente-central-compra-participante-form.component.html',
    styleUrls: ['./cliente-central-compra-participante-form.component.less'],
    providers: [
        ClienteNomePipe
    ]
})
export class ClienteCentralCompraParticipanteFormComponent {
    /** Se pode editar os participantes da central de compras */
    @Input() canEdit = false;

    /** Clientes participantes da central de compras */
    @Input() entities: ClienteCentralCompraParticipante[];
    @Output() entitiesChange = new EventEmitter<ClienteCentralCompraParticipante[]>();
    /** Percentual de participação total dos participantes da central de compras */
    @Input() percParticipacaoTotalCentralCompra: number;
    @Output() percParticipacaoTotalCentralCompraChange = new EventEmitter<number>();

    @ViewChild('form') form: NgForm;
    @ViewChildren('typeaheadParticipante') typeaheadsParticipantes: QueryList<TypeaheadClienteComponent>;
    @ViewChildren('inputPercParticipacao') inputsPercParticipacao: QueryList<Decimal2Component>;

    constructor(private messageService: MessageService,
                private formsUtilsService: FormsUtilsService,
                private clienteNomePipe: ClienteNomePipe,
                private clienteCentralCompraParticipanteCalculoService: ClienteCentralCompraParticipanteCalculoService) { }

    /** Insere um participante */
    insert(participante: ClienteCentralCompraParticipante = null) {
        if (!this.entities) {
            this.entities = [];
            this.entitiesChange.emit(this.entities);
        }

        if (participante) {
            this.entities.push(participante);
            setTimeout(() => this.setFocusInpuPercParticipacao(this.entities.length - 1), 200);
            this.calcularPercParticipacaoTotal();
        } else {
            const entity = new ClienteCentralCompraParticipante();
            this.entities.push(entity);
            setTimeout(() => this.typeaheadsParticipantes?.last?.setFocus(), 200);
        }

    }

    /** Remove um participante */
    delete(entity: ClienteCentralCompraParticipante, index: number) {
        if (index < 0) {
            return;
        }

        this.messageService.confirm(`Deseja realmente remover o participante ${this.clienteNomePipe.transform(entity?.nome, entity?.nomeFantasia, entity?.codigoIntegracaoErp)} da central de compras?`).then(result => {
            if (result) {
                this.entities.splice(index, 1);
                this.calcularPercParticipacaoTotal();
            }
        });
    }

    /** Retorna os ID's dos participantes que devem ser ignorados no typeahead para adicionar novos participantes na central de compras */
    getIdsParticipantesIgnore(): number[] {
        const ids: number[] = [];
        this.entities?.forEach(entity => {
            if (entity.id > 0) {
                ids.push(entity.id);
            }
        });

        return ids;
    }

    /** Ao selecionar um participante */
    onSelectParticipante(entity: ClienteCentralCompraParticipante, index: number) {
        if (entity?.clientePariticipante) {
            entity.id = entity.clientePariticipante.id;
            entity.idPessoa = entity.clientePariticipante.idPessoa;
            entity.codigo = entity.clientePariticipante.pessoa?.codigo;
            entity.codigoIntegracaoErp = entity.clientePariticipante.pessoa?.codigoIntegracaoErp;
            entity.tipo = entity.clientePariticipante.pessoa?.tipo;
            entity.cnpjCpf = entity.clientePariticipante.pessoa?.cnpjCpf;
            entity.nome = entity.clientePariticipante.pessoa?.nome;
            entity.nomeFantasia = entity.clientePariticipante.pessoa?.nomeFantasia;
        } else {
             this.clearParticipante(entity);
        }

        // Setando foco no input do percentual de participação
        setTimeout(() => this.setFocusInpuPercParticipacao(index), 200);
    }

    /** Remove as informações do participante */
    clearParticipante(entity: ClienteCentralCompraParticipante) {
        if (!entity) {
            return;
        }

        entity.id = null;
        entity.idPessoa = null;
        entity.codigo = null;
        entity.codigoIntegracaoErp = null;
        entity.tipo = null;
        entity.cnpjCpf = null;
        entity.nome = null;
        entity.nomeFantasia = null;
    }

    /** Calcular o percentual de participação total dos participantes de uma central de compras */
    private calcularPercParticipacaoTotal() {
        this.percParticipacaoTotalCentralCompra = this.clienteCentralCompraParticipanteCalculoService.calcularPercParticipacaoTotal(this.entities);
        this.percParticipacaoTotalCentralCompraChange.emit(this.percParticipacaoTotalCentralCompra);
    }

    /** Seta o foco no input do percentual de participação */
    private setFocusInpuPercParticipacao(index: number) {
        this.inputsPercParticipacao?.last?.setFocus();
    }

    /** Ao alterar o percentual de participação */
    onChangePerParticipacao() {
        // Calculando o percentual de participação total dos participantes da central de compras
        this.calcularPercParticipacaoTotal();
    }

    /** Verifica os participantes da central de compras estão válidos */
    isValid(): boolean {
        this.formsUtilsService.makeAllFieldsDirty(this.form);

        // Duplicações
        for (let i = 0; i < this.entities.length; i++) {
            const entity = this.entities[i];

            // Participante não informado
            if (!entity.id) {
                this.messageService.warning('É necessário selecionar o participante da central de compras ou remover o registro não informado', 'Atenção', notCloseMessageOptions);
                this.typeaheadsParticipantes?.find((item, index) => index === i)?.setFocus();
                return false;
            }
            // Participante duplicado
            const count = this.entities.filter(entityFilter => entityFilter.id && entityFilter.id === entity.id).length;
            if (count > 1) {
                this.messageService.warning(`O participante ${this.clienteNomePipe.transform(entity?.nome, entity?.nomeFantasia, entity?.codigoIntegracaoErp)} já está adicionado a central de compras. Remova ou altere este participante duplicado`, 'Não é possível adicionar duas vezes o mesmo participante na central de compras', notCloseMessageOptions);
                this.typeaheadsParticipantes?.find((item, index) => index === i)?.setFocus();
                return false;
            }
        }

        // Form
        if (!this.form.valid) {
            this.messageService.warning('Preencha corretamente todos os participantes da central de compras', 'Atenção', notCloseMessageOptions);
            return false;
        }

        return true;
    }
}
